import { Check, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { BillingIntervalToggle } from './BillingIntervalToggle';
import {
  FREE_PLAN_FEATURES,
  PRO_PLAN_FEATURES,
  proPricePresentation,
  type AppBillingInterval,
} from '../billingPlans';

interface PlanCardProps {
  name: string;
  price: string;
  detail?: string;
  features: readonly string[];
  highlighted?: boolean;
}

function PlanCard({ name, price, detail, features, highlighted = false }: PlanCardProps) {
  return (
    <section
      className={`rounded-lg border p-4 sm:p-5 ${
        highlighted
          ? 'border-primary bg-primary/[0.06]'
          : 'border-border bg-background'
      }`}
      aria-label={`${name} plan`}
    >
      <div className="flex items-baseline justify-between gap-3">
        <h3 className={`text-base font-semibold ${highlighted ? 'text-primary' : ''}`}>
          {name}
        </h3>
        <span className="shrink-0 text-sm font-semibold">{price}</span>
      </div>
      {detail ? <p className="mt-1 text-right text-xs text-muted-foreground">{detail}</p> : null}
      <ul className="mt-4 space-y-2.5">
        {features.map((feature) => (
          <li key={feature} className="flex items-start gap-2 text-sm leading-5">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
            <span>{feature}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

interface PlanModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  interval: AppBillingInterval;
  onIntervalChange: (interval: AppBillingInterval) => void;
  onUpgrade: (interval: AppBillingInterval) => void;
  loading?: boolean;
}

export function PlanModal({
  open,
  onOpenChange,
  interval,
  onIntervalChange,
  onUpgrade,
  loading = false,
}: PlanModalProps) {
  const price = proPricePresentation(interval);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="plan-modal"
        overlayClassName="bg-black/40"
        className="w-[calc(100%-2rem)] max-w-[645px] gap-0 rounded-xl p-5 sm:p-8 max-sm:bottom-0 max-sm:top-auto max-sm:translate-y-0 max-sm:rounded-b-none max-sm:rounded-t-2xl"
      >
        <div className="space-y-1">
          <DialogTitle className="text-xl sm:text-2xl">Choose your plan</DialogTitle>
          <DialogDescription>Plans apply to this workspace</DialogDescription>
        </div>

        <div className="mt-5">
          <BillingIntervalToggle
            value={interval}
            onChange={onIntervalChange}
            disabled={loading}
          />
        </div>

        <div className="mt-5 grid gap-3 sm:grid-cols-2 sm:gap-5">
          <PlanCard
            name="FREE"
            price="Free"
            features={FREE_PLAN_FEATURES}
          />
          <PlanCard
            name="PRO"
            price={price.modalPrice}
            detail={price.detail}
            highlighted
            features={PRO_PLAN_FEATURES}
          />
        </div>

        <Button
          data-testid="plan-modal-upgrade-button"
          type="button"
          className="mx-auto mt-5 min-w-44"
          onClick={() => onUpgrade(interval)}
          disabled={loading}
        >
          {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          {loading ? 'Opening Checkout...' : 'Upgrade to Pro'}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
